import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import { isGuestPortalRole } from '../utils/portalRoles';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const clearLocalSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('activeCompanyId');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const role = user?.role ? String(user.role).trim().toLowerCase() : '';
  const isSuperadmin = role === 'superadmin';
  /** Company role: external auditor — read policies / controls / evidence only */
  const isExternalAuditor = role === 'auditor';
  /** Company role: guest — read-only; only policies shared by the organisation */
  const isGuest = isGuestPortalRole(role);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyMeResponse = useCallback((data) => {
    setUser(data.user);
    setCompany(data.company);

    if (data.user?.role && String(data.user.role).trim().toLowerCase() === 'superadmin') {
      const list = data.companies || [];
      setCompanies(list);
      const savedCompanyId = localStorage.getItem('activeCompanyId');
      if (savedCompanyId) {
        const found = list.find((c) => c.id === savedCompanyId);
        if (found) {
          setActiveCompanyId(savedCompanyId);
          setCompany(found);
        }
      }
    } else if (data.user?.company_id) {
      setActiveCompanyId(data.user.company_id);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const data = await apiService.request('/auth/me', { timeoutMs: 15000 });
        applyMeResponse(data);
      } catch (err) {
        if (err?.name === 'AbortError') {
          console.warn('Auth check timed out — clearing session and showing sign-in');
        } else if (err?.status === 401) {
          // Token rejected — silent clear, normal case after expiry / logout
        } else {
          console.error('Auth check failed:', err);
        }
        clearLocalSession();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, totpToken) => {
    try {
      setError(null);
      setLoading(true);

      const data = await apiService.login(email, password, totpToken);

      // MFA second step required
      if (data?.totp_required) {
        return { success: false, totp_required: true };
      }

      if (data?.success) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        setCompany(data.company);

        if (String(data.user.role || '').trim().toLowerCase() === 'superadmin') {
          setCompanies(data.companies || []);
          setActiveCompanyId(null);
        } else {
          setActiveCompanyId(data.user.company_id);
        }

        return { success: true, role: data.user.role };
      }

      const message = data?.message || 'Login failed.';
      setError(message);
      return { success: false, error: message };
    } catch (err) {
      const message = err?.body?.message || 'Login failed. Please try again.';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      setLoading(true);

      const data = await apiService.register(userData);

      if (data?.success) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        setCompany(data.company);
        setActiveCompanyId(data.user.company_id);
        return { success: true };
      }

      const message = data?.message || 'Registration failed.';
      setError(message);
      return { success: false, error: message };
    } catch (err) {
      const message = err?.body?.message || 'Registration failed. Please try again.';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const switchCompany = useCallback((companyId) => {
    if (!isSuperadmin) return;

    if (companyId) {
      const found = companies.find((c) => c.id === companyId);
      if (found) {
        setActiveCompanyId(companyId);
        setCompany(found);
        localStorage.setItem('activeCompanyId', companyId);
      }
    } else {
      setActiveCompanyId(null);
      setCompany(null);
      localStorage.removeItem('activeCompanyId');
    }
  }, [isSuperadmin, companies]);

  const refreshCompanies = useCallback(async () => {
    if (!isSuperadmin) return;
    try {
      const data = await apiService.getMe();
      const list = data?.companies || [];
      setCompanies(list);
      const savedId = localStorage.getItem('activeCompanyId');
      if (savedId) {
        const found = list.find((c) => c.id === savedId);
        if (found) setCompany(found);
      }
    } catch (err) {
      console.error('Failed to refresh companies:', err);
    }
  }, [isSuperadmin]);

  const logout = useCallback(async () => {
    try {
      // Best-effort: tells the server to add the bearer token to its denylist.
      // Errors are non-fatal — we always clear the local session.
      await apiService.logout();
    } catch (err) {
      // Token may already be invalid / expired; ignore.
    } finally {
      clearLocalSession();
      setUser(null);
      setCompany(null);
      setCompanies([]);
      setActiveCompanyId(null);
      setError(null);
    }
  }, []);

  const value = {
    user,
    company,
    companies,
    activeCompanyId,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isSuperadmin,
    isAdmin: role === 'admin' || role === 'superadmin',
    /** Team tab under `/profile/team`: admins and org control owners (default invited role). */
    canManageTeam: role === 'admin' || role === 'superadmin' || role === 'control_owner',
    isExternalAuditor,
    isGuest,
    // Deprecated alias — same as isGuest; avoids stale `isClient` shorthand / destructuring errors
    isClient: isGuest,
    switchCompany,
    refreshCompanies,
    setError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
